import * as React from "react";
import { buildMethodJsonLd, buildHreflangLinks } from "@klapzo/kit-core";
import { getMethod, useRemote, type MethodPage } from "../kitApi.js";
import { getLocale, t } from "../i18n.js";
import { KitShell, RemoteError, FavouriteButton } from "../components.js";
import { Markdown } from "../markdown.js";
import { addBlock } from "../agendaStore.js";
import { loadFavourites, toggleFavourite } from "../favouritesStore.js";

const KIT_BASE_URL = (import.meta.env.VITE_KIT_BASE_URL as string | undefined) ?? `${location.origin}/kit`;

export function MethodDetail({ slug }: { slug: string }): React.ReactElement {
  const locale = getLocale();
  const [favourites, setFavourites] = React.useState<Set<string>>(() => loadFavourites());
  const remote = useRemote<MethodPage | null>((signal) => getMethod(slug, locale, signal), [slug, locale]);
  const page = remote.state === "ready" ? remote.data : null;

  // SEO/GEO: inject JSON-LD HowTo + hreflang alternates into <head> (task 2.3). Still client-side,
  // so it depends on the crawler executing JS — prerendering is a separate proposal, not fixed here.
  React.useEffect(() => {
    if (!page) return;
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.text = JSON.stringify(buildMethodJsonLd(page.method));
    document.head.appendChild(ld);

    const links = buildHreflangLinks(slug, page.locales, KIT_BASE_URL).map((l) => {
      const el = document.createElement("link");
      el.rel = "alternate";
      el.hreflang = l.hreflang;
      el.href = l.href;
      document.head.appendChild(el);
      return el;
    });
    // Restore on unmount: KitShell navigates with react-router <Link>, so leaving this page never
    // reloads the document — without this the tab keeps the method's name on /kit and /kit/planner.
    const previousTitle = document.title;
    document.title = `${page.method.title} — Klapzo Kit`;
    return () => {
      ld.remove();
      links.forEach((el) => el.remove());
      document.title = previousTitle;
    };
  }, [slug, page]);

  if (remote.state === "loading") {
    return (
      <KitShell>
        <p>{t("kit.loading")}</p>
      </KitShell>
    );
  }

  // An unknown slug is a 404 page; a failed fetch is an error the visitor can retry. Collapsing the
  // two would tell a visitor a method does not exist every time kit-api hiccups.
  if (remote.state === "error") {
    return (
      <KitShell>
        <a href="/kit" className="text-accent underline">{t("detail.back")}</a>
        <RemoteError message={t("kit.error")} retry={t("kit.retry")} />
      </KitShell>
    );
  }

  if (!page) {
    return (
      <KitShell>
        <a href="/kit" className="text-accent underline">{t("detail.back")}</a>
        <p>404</p>
      </KitShell>
    );
  }

  const { method } = page;
  return (
    <KitShell>
      <a href="/kit" className="text-accent underline">{t("detail.back")}</a>
      <div className="flex items-start justify-between gap-3">
        <h1 className="font-display text-display-md">{method.title}</h1>
        <FavouriteButton
          active={favourites.has(slug)}
          onToggle={() => setFavourites(toggleFavourite(slug))}
          label={t(favourites.has(slug) ? "detail.unfavourite" : "detail.favourite")}
        />
      </div>
      <div className="flex flex-wrap gap-2 text-body-sm">
        <span className="rounded-md bg-surface px-2 py-1">{t(`mode.${method.mode}`)}</span>
        {method.durationMin != null && <span className="rounded-md bg-surface px-2 py-1">{method.durationMin} {t("planner.min")}</span>}
        {method.groupSize && <span className="rounded-md bg-surface px-2 py-1">👥 {method.groupSize}</span>}
        {method.tags.map((tag) => (
          <span key={tag} className="rounded-full bg-surface px-2 py-1">{tag}</span>
        ))}
      </div>

      <Markdown md={method.bodyMd} />

      {(method.source || method.license) && (
        <p className="text-body-sm text-muted">
          {t("detail.source")}: {method.source ?? "—"} · {method.license}
        </p>
      )}

      <button onClick={() => addBlock(method)} className="self-start rounded-md bg-primary px-4 py-2 font-bold text-primary-foreground">
        {t("detail.add")}
      </button>

      {/* Ranked server-side by tag overlap; empty rather than padded with unrelated methods,
          because a "related" list nobody trusts is a list nobody reads. */}
      {page.related.length > 0 && (
        <section className="flex flex-col gap-2 border-t border-border pt-4">
          <h2 className="font-display text-display-sm">{t("detail.related")}</h2>
          <ul className="grid gap-2 sm:grid-cols-2">
            {page.related.map((r) => (
              <li key={r.slug} className="rounded-md border border-border p-3">
                <a href={`/kit/m/${r.slug}`} className="text-accent underline">{r.title}</a>
                <div className="text-body-sm text-muted">
                  {t(`mode.${r.mode}`)}
                  {r.durationMin != null && ` · ${r.durationMin} ${t("planner.min")}`}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </KitShell>
  );
}
